import React, { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Radio } from '@node-space/ui-components'
import { DepositBankAccount } from 'types/BankAccountsDetails'
import { BankTransferType } from 'types/beneficiaries'
import { Currencies, WalletType } from 'types/types'
import { getPaymentTitle } from 'utils/wallet'
import LocalDetails from './LocalDetails'
import SwiftDetails from './SwiftDetails'

interface SingleDepositDetailsProps {
  bankAccounts: DepositBankAccount[]
  wallet: WalletType
}

const MultipleDepositDetails = ({ bankAccounts, wallet }: SingleDepositDetailsProps) => {
  const { t } = useTranslation()

  const [selectedBankAccount, setSelectedBankAccount] = useState<number>(0)

  const localDetails = bankAccounts?.filter(account => !account?.isSwift)
  const swiftDetails = bankAccounts?.filter(account => account?.isSwift)
  const hasMultipleLocalDetails = localDetails?.length > 1
  const hasMultipleSwiftDetails = swiftDetails?.length > 1

  const options = useMemo(() => {
    const localDetailsOptions = localDetails?.map((account, index) => ({
      label: getPaymentTitle(account, t, hasMultipleLocalDetails ? index : undefined),
      value: `local-${index}-${account?.accountNumber}`,
    }))

    const swiftDetailsOptions = swiftDetails?.map((account, index) => ({
      label: getPaymentTitle(account, t, hasMultipleSwiftDetails ? index : undefined),
      value: `swift-${index}-${account?.accountNumber}`,
    }))

    return [...localDetailsOptions, ...swiftDetailsOptions]?.map((option, index) => ({
      ...option,
      value: index?.toString(),
    }))
  }, [localDetails, swiftDetails, hasMultipleLocalDetails, hasMultipleSwiftDetails])

  const handleOnDepositAccountChange = (value: string) => {
    setSelectedBankAccount(Number(value))
  }

  return (
    <>
      <Radio
        name="depositAccount"
        horizontal={options?.length < 4}
        onChange={value => handleOnDepositAccountChange(value)}
        key="depositAccount"
        testId="depositAccount"
        options={options}
        value={selectedBankAccount?.toString()}
        border={options?.length < 4}
      />

      <>
        {bankAccounts?.[selectedBankAccount]?.isSwift ||
        bankAccounts?.[selectedBankAccount]?.accountNumberFormat === BankTransferType.SWIFT ? (
          <SwiftDetails options={bankAccounts?.[selectedBankAccount]} />
        ) : (
          <LocalDetails
            currency={wallet?.currency?.code as Currencies}
            options={bankAccounts?.[selectedBankAccount]}
            wallet={wallet}
            isSegregated
          />
        )}
      </>
    </>
  )
}

export default memo(MultipleDepositDetails)
